import type { ProgramQuote } from '@makemydeal/dr-dash-types';
import type { ProgramFilters, FilterOption, FilterOptionsByKey, AppliedFilterTag } from './ProgramsFilters.types';
import { FILTER_CONFIGS, getVisibleFilterConfigs } from './ProgramsFilters.types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { CREDIT_TIER_OPTIONS, ALL_CREDIT_TIERS_OPTION, ALL_CREDIT_TIERS_VALUE } from './creditTiers';

interface ExtractorConfig<T> {
    getValue: (program: ProgramQuote) => T | undefined | null;
    formatLabel?: (value: T) => string;
    sortFn?: (a: T, b: T) => number;
}

// Generic helper to extract unique filter options from programs
const extractFilterOptions = <T extends string | number>(programs: ProgramQuote[], config: ExtractorConfig<T>): FilterOption[] => {
    const valuesSet = new Set<T>();

    programs.forEach((program) => {
        const value = config.getValue(program);
        if (value !== undefined && value !== null && !valuesSet.has(value)) {
            valuesSet.add(value);
        }
    });

    const sortedValues = Array.from(valuesSet);
    if (config.sortFn) {
        sortedValues.sort(config.sortFn);
    }

    return sortedValues.map((value) => ({
        value: String(value),
        label: config.formatLabel ? config.formatLabel(value) : String(value)
    }));
};

// Extracts unique lender options from programs
export const extractLenderOptions = (programs: ProgramQuote[]): FilterOption[] => {
    return extractFilterOptions(programs, {
        getValue: (program) => program.lenderCode,
        sortFn: (a, b) => a.localeCompare(b)
    });
};

// Extracts unique term options from programs
export const extractTermOptions = (programs: ProgramQuote[]): FilterOption[] => {
    return extractFilterOptions(programs, {
        getValue: (program) => (program.term !== undefined && program.term > 0 ? program.term : undefined),
        sortFn: (a, b) => a - b
    });
};

// Extracts unique miles per year options from programs (lease only)
export const extractMilesPerYearOptions = (programs: ProgramQuote[]): FilterOption[] => {
    return extractFilterOptions(programs, {
        getValue: (program) => program.annualMiles,
        formatLabel: (value) => value.toLocaleString(),
        sortFn: (a, b) => a - b
    });
};

export const extractRateProgramOptions = (programs: ProgramQuote[], isPIScreenEnhancementsEnabled = false): FilterOption[] => {
    if (isPIScreenEnhancementsEnabled) {
        return extractFilterOptions(programs, {
            getValue: (program) => program.lenderProgram?.rateProgram || undefined,
            sortFn: (a, b) => a.localeCompare(b)
        });
    }
    return extractFilterOptions(programs, {
        getValue: (program) => program.rateType,
        sortFn: (a, b) => a.localeCompare(b)
    });
};

export const extractCreditTierOptions = (_programs: ProgramQuote[]): FilterOption[] => {
    return [ALL_CREDIT_TIERS_OPTION, ...CREDIT_TIER_OPTIONS.map(({ value, label }) => ({ value, label }))];
};

export const getLenderTier = (program: ProgramQuote): string | undefined =>
    program.creditTierDescription?.trim().replace(/\s+/g, ' ') || undefined;

const LENDER_TIER_SEPARATOR = '::';

const buildLenderTierValue = (lenderCode: string, tier: string): string => `${lenderCode}${LENDER_TIER_SEPARATOR}${tier}`;

const foldLenderTierValue = (value: string): string => value.toLocaleLowerCase();

const getFoldedLenderTierValue = (program: ProgramQuote): string | undefined => {
    const tier = getLenderTier(program);
    return program.lenderCode && tier ? foldLenderTierValue(buildLenderTierValue(program.lenderCode, tier)) : undefined;
};

const formatLenderTierValue = (value: string): string => value.split(LENDER_TIER_SEPARATOR).join(' - ');

const naturalCompare = (a: string, b: string): number => a.localeCompare(b, undefined, { numeric: true });

const UNRANKED_TIER = Number.MAX_SAFE_INTEGER;

export const extractLenderTierOptions = (programs: ProgramQuote[]): FilterOption[] => {
    const nameByCode = new Map<string, string>();
    programs.forEach((program) => {
        if (!program.lenderCode) return;
        const legalName = program.lenderLegalName?.trim();
        if (legalName) {
            nameByCode.set(program.lenderCode, legalName);
        } else if (!nameByCode.has(program.lenderCode)) {
            nameByCode.set(program.lenderCode, program.lenderCode);
        }
    });

    const byValue = new Map<string, { option: FilterOption & { group: string }; rank: number }>();

    programs.forEach((program) => {
        const tier = getLenderTier(program);
        const lenderCode = program.lenderCode;
        if (!tier || !lenderCode) return;

        const value = foldLenderTierValue(buildLenderTierValue(lenderCode, tier));
        const rank = program.creditTier ?? UNRANKED_TIER;
        const existing = byValue.get(value);
        if (existing) {
            existing.rank = Math.min(existing.rank, rank);
            return;
        }

        const group = nameByCode.get(lenderCode) as string;
        byValue.set(value, { option: { value, label: tier, group }, rank });
    });

    const rankedOptions = Array.from(byValue.values());
    rankedOptions.sort(
        (a, b) =>
            naturalCompare(a.option.group, b.option.group) || a.rank - b.rank || naturalCompare(a.option.label, b.option.label)
    );
    return rankedOptions.map(({ option }) => option);
};

const OPTIONS_EXTRACTORS: Record<
    keyof ProgramFilters,
    (programs: ProgramQuote[], isPIScreenEnhancementsEnabled: boolean) => FilterOption[]
> = {
    lenders: extractLenderOptions,
    terms: extractTermOptions,
    milesPerYear: extractMilesPerYearOptions,
    ratePrograms: extractRateProgramOptions,
    creditTiers: extractCreditTierOptions,
    lenderTiers: extractLenderTierOptions
};

export const extractAllFilterOptions = (programs: ProgramQuote[], isPIScreenEnhancementsEnabled: boolean): FilterOptionsByKey => {
    const enabledKeys = new Set(getVisibleFilterConfigs(true, isPIScreenEnhancementsEnabled).map((config) => config.filterKey));
    return (Object.keys(OPTIONS_EXTRACTORS) as Array<keyof ProgramFilters>).reduce(
        (options, filterKey) => ({
            ...options,
            [filterKey]: enabledKeys.has(filterKey) ? OPTIONS_EXTRACTORS[filterKey](programs, isPIScreenEnhancementsEnabled) : []
        }),
        {} as FilterOptionsByKey
    );
};

export const getEnabledFilters = (filters: ProgramFilters, isPIScreenEnhancementsEnabled: boolean): ProgramFilters => {
    if (isPIScreenEnhancementsEnabled) return filters;
    return FILTER_CONFIGS.reduce(
        (enabled, config) => (config.requiresPIScreenEnhancements ? { ...enabled, [config.filterKey]: [] } : enabled),
        filters
    );
};

const matchesSelectedLenderTiers = (program: ProgramQuote, selectedLenderTiers: Set<string>): boolean => {
    if (selectedLenderTiers.size === 0) return true;
    const value = getFoldedLenderTierValue(program);
    return value !== undefined && selectedLenderTiers.has(value);
};

export const filterPrograms = (
    programs: ProgramQuote[],
    appliedFilters: ProgramFilters,
    offerType: string,
    isPIScreenEnhancementsEnabled: boolean
): ProgramQuote[] => {
    const filters = getEnabledFilters(appliedFilters, isPIScreenEnhancementsEnabled);
    const selectedLenderTiers = new Set(filters.lenderTiers.map(foldLenderTierValue));

    return programs.filter((program) => {
        if (filters.creditTiers.length > 0) {
            const selectedTier = filters.creditTiers[0];
            if (
                selectedTier !== ALL_CREDIT_TIERS_VALUE &&
                program.requestedTier != null &&
                program.requestedTier !== selectedTier
            ) {
                return false;
            }
        }

        if (!matchesSelectedLenderTiers(program, selectedLenderTiers)) {
            return false;
        }

        if (filters.lenders.length > 0) {
            if (!program.lenderCode || !filters.lenders.includes(program.lenderCode)) {
                return false;
            }
        }

        if (filters.terms.length > 0) {
            if (program.term === undefined || program.term === null || !filters.terms.includes(program.term.toString())) {
                return false;
            }
        }

        if (offerType === LEASE) {
            if (filters.milesPerYear.length > 0) {
                if (
                    program.annualMiles === undefined ||
                    program.annualMiles === null ||
                    !filters.milesPerYear.includes(program.annualMiles.toString())
                ) {
                    return false;
                }
            }
        }

        if (isPIScreenEnhancementsEnabled) {
            if (filters.ratePrograms.length > 0) {
                const rp = program.lenderProgram?.rateProgram || undefined;
                if (!rp || !filters.ratePrograms.includes(rp)) {
                    return false;
                }
            }
        } else if (offerType === LEASE) {
            if (filters.ratePrograms.length > 0) {
                if (!program.rateType || !filters.ratePrograms.includes(program.rateType)) {
                    return false;
                }
            }
        }

        return true;
    });
};

export const countActiveFilters = (filters: ProgramFilters, offerType: string, isPIScreenEnhancementsEnabled: boolean): number => {
    const isLease = offerType === LEASE;
    const visibleFilters = getVisibleFilterConfigs(isLease, isPIScreenEnhancementsEnabled);

    return visibleFilters.reduce((count, config) => {
        const values = filters[config.filterKey];
        const activeValues =
            config.filterKey === 'creditTiers' ? values.filter((value) => value !== ALL_CREDIT_TIERS_VALUE) : values;
        return count + activeValues.length;
    }, 0);
};

export const hasActiveFilters = (filters: ProgramFilters, offerType: string, isPIScreenEnhancementsEnabled: boolean): boolean => {
    return countActiveFilters(filters, offerType, isPIScreenEnhancementsEnabled) > 0;
};

const isSameOptionValue = (filterKey: keyof ProgramFilters, a: string, b: string): boolean =>
    filterKey === 'lenderTiers' ? foldLenderTierValue(a) === foldLenderTierValue(b) : a === b;

export const getAppliedFilterTags = (
    filters: ProgramFilters,
    offerType: string,
    filterOptions: FilterOptionsByKey,
    isPIScreenEnhancementsEnabled: boolean
): AppliedFilterTag[] => {
    const isLease = offerType === LEASE;
    const visibleFilters = getVisibleFilterConfigs(isLease, isPIScreenEnhancementsEnabled);

    const tags: AppliedFilterTag[] = [];

    visibleFilters.forEach((config) => {
        const filterKey = config.filterKey;
        const options = filterOptions[filterKey];

        filters[filterKey].forEach((value) => {
            if (filterKey === 'creditTiers' && value === ALL_CREDIT_TIERS_VALUE) return;
            const option = options.find((opt) => isSameOptionValue(filterKey, opt.value, value));
            const fallbackLabel = filterKey === 'lenderTiers' ? formatLenderTierValue(value) : value;
            tags.push({
                filterKey,
                value,
                label: option?.group ? `${option.group} - ${option.label}` : option?.label || fallbackLabel
            });
        });
    });

    return tags;
};

// Removes a specific value from a filter category
export const removeFilterValue = (filters: ProgramFilters, filterKey: keyof ProgramFilters, value: string): ProgramFilters => {
    return {
        ...filters,
        [filterKey]: filters[filterKey].filter((v) => v !== value)
    };
};

// Toggles a filter value (adds if not present, removes if present)
export const toggleFilterValue = (currentValues: string[], value: string): string[] => {
    if (currentValues.includes(value)) {
        return currentValues.filter((v) => v !== value);
    }
    return [...currentValues, value];
};

export const setFilterValue = (value: string): string[] => {
    return value ? [value] : [];
};
